// eslint-disable-next-line @altermaind/you-shall-not-pass
import { FC, useEffect, useState } from 'react';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Loader, Typography } from '@altermaind/web/ui';
import { AlertBadge } from '@/_shared/components/AlertBadge';
import { Employer } from '@/people/state/types';
import { useSurvey } from '@/surveys';
import { ChipsTemplate } from '@/surveys/components';
import { getSurveysCampaignsCall, postEmployerByIdSurveyResultCall } from '@/surveys/state/api';
import { EnrollmentStatus } from '@/swagger/constants/surveys-enums';
import { SurveyCompleted } from './SurveyCompleted';
import { SurveyInvited } from './SurveyInvited';
import { SurveyNotInvited } from './SurveyNotInvited';

type Props = {
  employer: Employer;
};

export const EmployeeAssessment: FC<Props> = ({ employer }) => {
  const t = useTranslations('department.details.assessment.noData');
  const tShared = useTranslations();

  const [activeChip, setActiveChip] = useState<string>('');

  const { organizationHasCampaignsHardSkill, organizationHasCampaignsSoftSkill } = useSurvey();

  const {
    execute: executePostEmployerByIdSurveyResult,
    loading: loadingPostEmployerByIdSurveyResult,
    success: successPostEmployerByIdSurveyResult,
    error: errorPostEmployerByIdSurveyResult,
    response: responsePostEmployerByIdSurveyResult,
  } = postEmployerByIdSurveyResultCall.hook();

  const { response: responseGetSurveysCampaigns } = getSurveysCampaignsCall.hook();

  useEffect(() => {
    if (employer.email) {
      executePostEmployerByIdSurveyResult({
        username: employer.email,
      });
    }
  }, [employer.email, executePostEmployerByIdSurveyResult]);

  useEffect(() => {
    if (
      responsePostEmployerByIdSurveyResult &&
      responseGetSurveysCampaigns &&
      responsePostEmployerByIdSurveyResult.length > 0
    ) {
      if (!organizationHasCampaignsHardSkill || !organizationHasCampaignsSoftSkill) {
        setActiveChip(responsePostEmployerByIdSurveyResult[0].templateName ?? '');
      }
    }
  }, [
    organizationHasCampaignsHardSkill,
    organizationHasCampaignsSoftSkill,
    responsePostEmployerByIdSurveyResult,
    responseGetSurveysCampaigns,
  ]);

  if (loadingPostEmployerByIdSurveyResult) {
    return (
      <Box mt='s' textAlign='center'>
        <Loader size='s' />
      </Box>
    );
  }

  if (errorPostEmployerByIdSurveyResult) {
    const message = tShared('errors.genericApiError');
    return <AlertBadge containerProps={{ mt: 's' }} iconName='info' type='error' label={message} />;
  }

  if (employer.isComplete === false) {
    return (
      <Box mt='l' textAlign='center'>
        <Typography variant='h3'> {t('title')}</Typography>
        <Typography mb='s' variant='p3/light'>
          {t('descriptionEmployee')}
        </Typography>
      </Box>
    );
  }

  if (
    successPostEmployerByIdSurveyResult &&
    !loadingPostEmployerByIdSurveyResult &&
    !errorPostEmployerByIdSurveyResult &&
    employer
  ) {
    return (
      <Box>
        {responsePostEmployerByIdSurveyResult &&
          responsePostEmployerByIdSurveyResult?.length > 0 && (
            <Box mb='s'>
              <Typography variant='h1' mb='xs'>
                {employer.firstName} {employer.lastName}
              </Typography>
            </Box>
          )}
        {organizationHasCampaignsHardSkill && organizationHasCampaignsSoftSkill && (
          <ChipsTemplate setActiveChip={setActiveChip} activeChip={activeChip} />
        )}
        {responsePostEmployerByIdSurveyResult?.length === 0 && (
          <Box mt='l' textAlign='center'>
            <Typography variant='h3'> {t('title')}</Typography>
            <Typography mb='s' variant='p3/light'>
              {t('descriptionEmployee')}
            </Typography>
          </Box>
        )}
        {responsePostEmployerByIdSurveyResult
          ?.filter(survey => survey.templateName === activeChip)
          .map(survey => {
            return (
              <>
                {survey.enrollmentStatus === EnrollmentStatus.NOT_INVITED && (
                  <SurveyNotInvited survey={survey} />
                )}
                {survey.enrollmentStatus === EnrollmentStatus.INVITED && (
                  <SurveyInvited survey={survey} />
                )}
                {survey.enrollmentStatus === EnrollmentStatus.COMPLETED && (
                  <SurveyCompleted survey={survey} />
                )}
              </>
            );
          })}
      </Box>
    );
  }

  return null;
};
